import { useNavigate } from "react-router-dom";
import { Lock, PlayCircle, CheckCircle2, Radio, ClipboardCheck, Trophy, Video, Box } from "lucide-react";
import { LAB_PATH, LABS_LIST } from "@/lib/aiot";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/context/LanguageContext";

const Row = ({ active, onClick, icon, title, meta, testid, muted }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`w-full text-left flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${active ? "bg-[#0B1F3A] text-white" : "hover:bg-[#F0FDFA]"} ${muted && !active ? "opacity-70" : ""}`}>
    <span className={`mt-0.5 shrink-0 ${active ? "text-[#5EEAD4]" : "text-[#0F766E]"}`}>{icon}</span>
    <span className="min-w-0 flex-1"><span className="block text-[13px] font-semibold leading-snug">{title}</span><span className={`block text-[11px] ${active ? "text-slate-300" : "text-slate-500"}`}>{meta}</span></span>
  </button>
);

const classIcon = (c, enrolled, done) => {
  if (!enrolled) return <Lock className="w-4 h-4" />;
  if (done) return <CheckCircle2 className="w-4 h-4" />;
  if (c.has_live) return <Radio className="w-4 h-4" />;
  if (c.has_video) return <Video className="w-4 h-4" />;
  return <PlayCircle className="w-4 h-4" />;
};

export const PortalSidebar = ({ course, me, sel, onSelect }) => {
  const { L } = useLang();
  const enrolled = course.access.enrolled;
  const done = new Set(me?.lectures_done || []);
  const cur = course.classes.find((c) => c.id === sel);
  const nav = useNavigate();
  const labs = me?.result?.labs || {};
  return (
    <aside className="rounded-3xl border border-slate-200 bg-white p-3 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto" data-testid="aiot-portal-sidebar">
      <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{L("Your progress", "आपकी प्रगति")}</div>
      <Row active={sel === "tests"} onClick={() => onSelect("tests")} icon={enrolled ? <ClipboardCheck className="w-4 h-4" /> : <Lock className="w-4 h-4" />} title={L("Weekly tests", "साप्ताहिक टेस्ट")} meta={enrolled ? L(`${me?.result?.tests_done ?? 0} of 26 done`, `26 में से ${me?.result?.tests_done ?? 0} पूरे`) : L("Opens after approval", "मंज़ूरी के बाद खुलेगा")} testid="aiot-nav-tests" muted={!enrolled} />
      <Row active={sel === "result"} onClick={() => onSelect("result")} icon={enrolled ? <Trophy className="w-4 h-4" /> : <Lock className="w-4 h-4" />} title={L("My result", "मेरा रिज़ल्ट")} meta={L("Tests 40% · Labs 30% · Capstone 30%", "टेस्ट 40% · लैब 30% · कैपस्टोन 30%")} testid="aiot-nav-result" muted={!enrolled} />
      <button type="button" onClick={() => onSelect("labs")} className={`w-full text-left px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] hover:text-[#0F766E] ${sel === "labs" ? "text-[#0F766E]" : "text-slate-500"}`} data-testid="aiot-nav-labs">{L("3D automation labs", "3D ऑटोमेशन लैब")} →</button>
      {LABS_LIST.map(([k, en, hi]) => (
        <Row key={k} onClick={() => nav(LAB_PATH(k))} icon={<Box className="w-4 h-4" />} title={L(en, hi)} testid={`aiot-nav-lab-${k}`}
          meta={labs[k] != null ? L(`Best ${labs[k]}/100`, `सबसे अच्छा ${labs[k]}/100`) : enrolled ? L("Not attempted · counts 10%", "अभी नहीं किया · 10% गिना जाएगा") : L("Practice mode", "प्रैक्टिस मोड")} />
      ))}
      <div className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{L("Sample recorded classes", "सैंपल रिकॉर्डेड क्लास")}</div>
      {course.demos.map((d, i) => (
        <Row key={d.id} active={sel === d.id} onClick={() => onSelect(d.id)} icon={done.has(d.id) ? <CheckCircle2 className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
          title={`${i + 1}. ${L(d.title, d.title_hi)}`} meta={`${d.minutes} min · ${L("Sample · Recorded", "सैंपल · रिकॉर्डेड")}`} testid={`aiot-nav-${d.id}`} />
      ))}
      {enrolled ? <FullCourse course={course} sel={sel} cur={cur} done={done} onSelect={onSelect} /> : (
        <div className="m-2 mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center" data-testid="aiot-library-locked">
          <Lock className="w-5 h-5 mx-auto text-slate-500" />
          <div className="mt-2 text-[13px] font-bold text-[#0B1F3A]">{L("Full library · 78 classes", "पूरी लाइब्रेरी · 78 क्लास")}</div>
          <p className="mt-1 text-[11px] text-slate-500">{L("Recorded classes and Simran Ma'am's live classes open after enrollment.", "रिकॉर्डेड क्लास और सिमरन मैम की लाइव क्लास एनरोलमेंट के बाद खुलेंगी।")}</p>
        </div>
      )}
    </aside>
  );
};

const FullCourse = ({ course, sel, cur, done, onSelect }) => {
  const { L } = useLang();
  return (
    <>
      <div className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{L("Full course · 78 classes", "पूरा कोर्स · 78 क्लास")}</div>
      <Accordion type="single" collapsible defaultValue={`m${cur?.month || 1}`}>
        {course.months.map((m) => (
          <AccordionItem key={m.n} value={`m${m.n}`} className="border-0">
            <AccordionTrigger className="px-3 py-2.5 text-[13px] font-bold text-[#0B1F3A] hover:no-underline rounded-xl hover:bg-slate-50" data-testid={`aiot-nav-month-${m.n}`}>{L(`Month ${m.n}: ${m.title}`, `महीना ${m.n}: ${m.title_hi}`)}</AccordionTrigger>
            <AccordionContent className="pb-1">
              {course.classes.filter((c) => c.month === m.n).map((c) => (
                <Row key={c.id} active={sel === c.id} onClick={() => onSelect(c.id)} icon={classIcon(c, true, done.has(c.id))}
                  title={`${String(c.n).padStart(2, "0")}. ${L(c.title, c.title_hi)}`} meta={`${c.minutes_lecture || c.minutes} min${c.has_live ? " · live" : ""}`} testid={`aiot-nav-${c.id}`} />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
};
